/**
 * Date Range Processor
 * Fixes the backend API issue where day-wise data doesn't include proper month context
 */

export interface GraphDataPoint {
  day?: number;
  month?: number;
  year?: number;
  total: number;
  date?: string;
}

export interface ProcessedGraphData {
  date: string; // YYYY-MM-DD format
  day: number;
  month: number;
  year: number;
  total: number;
  label: string; // Formatted label for display
}

/**
 * Processes raw graph data from API to handle multi-month date ranges correctly
 */
export function processDateRangeData(
  rawData: GraphDataPoint[],
  fromDate: string,
  toDate: string,
  filter: 'daily' | 'weekly' | 'monthly'
): ProcessedGraphData[] {
  if (!rawData || rawData.length === 0) {
    return [];
  }

  // For monthly filter, the data is already correct
  if (filter === 'monthly') {
    return rawData.map(item => ({
      date: `${item.year || new Date().getFullYear()}-${String(item.month || 1).padStart(2, '0')}-01`,
      day: 1,
      month: item.month || 1,
      year: item.year || new Date().getFullYear(),
      total: item.total,
      label: `Month ${item.month}`
    }));
  }

  // For daily and weekly filters, we need to reconstruct proper dates
  const start = new Date(fromDate);
  const end = new Date(toDate);
  const processedData: ProcessedGraphData[] = [];

  // Create a map of existing data by day/month for quick lookup
  const dataMap = new Map<string, GraphDataPoint>();
  rawData.forEach(item => {
    if (item.day && item.month) {
      const key = `${item.day}-${item.month}`;
      dataMap.set(key, item);
    }
  });

  // Generate complete date range
  const currentDate = new Date(start);
  let dataIndex = 0;

  while (currentDate <= end) {
    const day = currentDate.getDate();
    const month = currentDate.getMonth() + 1; // JavaScript months are 0-indexed
    const year = currentDate.getFullYear();
    
    // Try to get data from the raw response first
    const dataKey = `${day}-${month}`;
    let total = 0;
    
    const existingData = dataMap.get(dataKey);
    if (existingData) {
      total = existingData.total;
    } else if (dataIndex < rawData.length) {
      // Use sequential data if available
      total = rawData[dataIndex].total;
      dataIndex++;
    }

    processedData.push({
      date: `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`,
      day,
      month,
      year,
      total,
      label: filter === 'daily' ? `${day}/${month}` : `Week ${Math.ceil(day / 7)}`
    });

    // Move to next day
    currentDate.setDate(currentDate.getDate() + 1);
  }

  return processedData;
}

/**
 * Preserves original data structure while fixing date context
 */
export function preserveOriginalDataStructure(
  originalData: any[],
  processedDates: ProcessedGraphData[]
): any[] {
  if (!originalData || originalData.length === 0) {
    return processedDates.map(item => ({
      day: item.day,
      month: item.month,
      year: item.year,
      total: item.total
    }));
  }

  return processedDates.map((processedItem, index) => {
    const originalItem = originalData[index % originalData.length];
    
    if (originalItem && typeof originalItem === 'object') {
      return {
        day: processedItem.day,
        month: processedItem.month,
        year: processedItem.year,
        total: processedItem.total,
        // Preserve any additional properties from the original data
        ...Object.keys(originalItem).reduce((acc, key) => {
          if (!['day', 'month', 'year', 'total'].includes(key)) {
            acc[key] = originalItem[key];
          }
          return acc;
        }, {} as any)
      };
    }
    
    return {
      day: processedItem.day,
      month: processedItem.month,
      year: processedItem.year,
      total: processedItem.total
    };
  });
}

/**
 * Generates proper labels for graph display based on filter type
 */
export function generateGraphLabel(
  data: ProcessedGraphData[],
  filter: 'daily' | 'weekly' | 'monthly',
  maxLabels: number = 12
): string[] {
  if (filter === 'monthly') {
    return data.map(item => item.label);
  }

  // For daily/weekly, show limited labels to avoid overcrowding
  const totalItems = data.length || 0;
  if (totalItems <= maxLabels) {
    return data.map(item => item.label);
  }

  // Show every nth label
  const step = Math.ceil(totalItems / maxLabels);
  return data.map((item, index) => {
    if (index % step === 0) {
      return item.label;
    }
    return '';
  });
}

/**
 * Validates and formats date range
 */
export function validateDateRange(fromDate: string, toDate: string): { isValid: boolean; error?: string } {
  if (!fromDate || !toDate) {
    return { isValid: false, error: 'Both from and to dates are required' };
  }

  const start = new Date(fromDate);
  const end = new Date(toDate);

  if (isNaN(start.getTime()) || isNaN(end.getTime())) {
    return { isValid: false, error: 'Invalid date format' };
  }

  if (start > end) {
    return { isValid: false, error: 'From date cannot be after to date' };
  }

  // Limit to reasonable range (e.g., 1 year)
  const maxRange = 365 * 24 * 60 * 60 * 1000; // 1 year in milliseconds
  if (end.getTime() - start.getTime() > maxRange) {
    return { isValid: false, error: 'Date range cannot exceed 1 year' };
  }

  return { isValid: true };
}
